import React, {
  useMemo, useEffect, useState, useCallback, ReactElement,
} from 'react';
import Table from '../../../../components/table/table.component';
import TableBody from '../../../../components/table-body/table-body.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import Widget from '../../../../components/widget/widget.component';
import ChipLink from '../../../../components/chip-link/chip-link.component';
import FieldTextNewItem from '../../../../components/field-text-new-item/field-text-new-item.component';
import FormFieldMultiSelect from '../../../../components/form-field-multi-select/form-field-multi-select.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import { SEARCH_RESULT_VIEW } from '../../../../constants/entity/permissions';

const ContactWidgetTags: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Tags';
  const WIDGET_NAME: string = 'widget-contact-tags';

  const {
    isEditMode,
    contactData,
    tagList,
    formPrefix,
    onTagClick,
    onTagsChange,
    onTagAddClick,
    onTagDeleteClick,
    onTagSearch,
    onTagSearchClose,
    onEditModeButtonClick,
  } = props;

  const [selectedTags, setSelectedTags]: [Tag | null, Function] = useState(null);

  const isEmpty: boolean = !contactData;
  const filteredTagList: string[] = tagList && tagList.length ? tagList.filter((tagItem: Tag): boolean => (
    contactData.tags && contactData.tags.length ? !contactData.tags.find((contactTagItem: Tag): boolean => (
      tagItem.name === contactTagItem.name
    )) : true
  )) : [];

  const menuItemTagList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionItemLabelKey: 'name',
    optionList: tagList,
    selectedItemList: selectedTags,
  }), [tagList, selectedTags]);

  const tagClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    tagId: number,
  ): void => {
    if (onTagClick) {
      onTagClick(event, tagId);
    }
  }, []);

  const tagsChange: Function = useCallback((tagValues: Tag[]): void => {
    if (onTagsChange) {
      onTagsChange(tagValues);
    }

    setSelectedTags(tagValues);
  }, []);

  useEffect((): void => {
    if (contactData) {
      if (!selectedTags) {
        setSelectedTags(contactData.tags || []);
      }
    }
  }, [contactData, isEditMode]);

  return (
    <Widget
      isFullWidth
      isFullHeight
      withEditMode
      isEmpty={isEmpty}
      isEditMode={isEditMode}
      widgetName={WIDGET_NAME}
      defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
      onEditModeButtonClick={onEditModeButtonClick}
    >
      <Table>
        <TableBody isPlain withZebraRows>
          <TableRow>
            <TableCell width="20%">
              Tags
            </TableCell>
            <TableCell>
              {
                isEditMode ? (
                  <FormFieldMultiSelect
                    isBordered
                    isAbleToAdd
                    withSearch
                    isDisabled={tagList && !tagList.length}
                    keyField="name"
                    name={`${formPrefix}.tags`}
                    optionList={tagList}
                    onSearch={onTagSearch}
                    onClose={onTagSearchClose}
                    onValuesChange={tagsChange}
                  >
                    {menuItemTagList}
                  </FormFieldMultiSelect>
                ) : (
                  contactData.tags.map((tagItem: Tag): ReactElement => (
                    <ChipLink
                      withDelete
                      isDisabled={isEditMode}
                      key={tagItem.id}
                      label={tagItem.name}
                      permission={SEARCH_RESULT_VIEW}
                      onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                        tagClick(event, tagItem.id);
                      }}
                      onDelete={(event: React.PointerEvent): void => {
                        if (onTagDeleteClick) {
                          onTagDeleteClick(event, tagItem.id);
                        }
                      }}
                    />
                  ))
                )
              }
              {
                !isEditMode && (
                  <FieldTextNewItem
                    tagList={filteredTagList}
                    itemLabelKey="name"
                    itemLabel="Tag"
                    onItemSearch={onTagSearch}
                    onItemSearchClose={onTagSearchClose}
                    onAddItem={onTagAddClick}
                  />
                )
              }
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Widget>
  );
});

export default ContactWidgetTags;
